<div class="author-filter space-y-3">
  <!-- Label and Clear Button -->
  <div class="flex items-center justify-between">
    <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">
      {{ label() }}
    </h3>
    @if (showClearButton() && hasSelection()) {
      <button
        type="button"
        (click)="clearSelection()"
        [disabled]="disabled()"
        class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 
               transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none 
               focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded px-1"
        aria-label="Clear author filter">
        Clear
      </button>
    }
  </div>

  <!-- Loading State -->
  @if (loading()) {
    <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2 animate-fadeIn">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400"></div>
      <span>{{ loadingMessage() }}</span>
    </div>
  }

  <!-- Error State -->
  @else if (error()) {
    <div class="space-y-2 animate-fadeIn">
      <div class="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
        <svg class="h-5 w-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ error() }}</span>
      </div>
      <button
        type="button"
        (click)="retryLoad()"
        class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 
               font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 
               focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded px-1"
        aria-label="Retry loading authors">
        Try again
      </button>
    </div>
  }

  <!-- Author Selection UI -->
  @else {
    <!-- Search Input (conditionally shown) -->
    @if (shouldShowSearch()) {
      <div class="relative">
        <input
          type="text"
          [value]="searchTerm()"
          (input)="onSearchChange($any($event.target).value)"
          [placeholder]="searchPlaceholder()"
          [disabled]="disabled()"
          class="w-full px-3 py-2 pr-10 text-sm border rounded-lg 
                 bg-white dark:bg-gray-800 
                 text-gray-900 dark:text-gray-100
                 border-gray-300 dark:border-gray-600
                 placeholder-gray-400 dark:placeholder-gray-500
                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                 focus:border-transparent
                 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-all"
          aria-label="Search authors">
        <svg class="absolute right-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" 
             fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    }

    <!-- Author Dropdown -->
    <div class="relative">
      <select
        [value]="selectedAuthorId() || ''"
        (change)="onAuthorSelect($any($event.target).value)"
        [disabled]="disabled()"
        class="w-full px-3 py-2 text-sm border rounded-lg 
               bg-white dark:bg-gray-800 
               text-gray-900 dark:text-gray-100
               border-gray-300 dark:border-gray-600
               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
               focus:border-transparent
               disabled:opacity-50 disabled:cursor-not-allowed
               transition-all cursor-pointer appearance-none"
        [class.pr-10]="true"
        aria-label="Select author">
        <option value="">{{ placeholder() }}</option>
        @for (author of filteredAuthors(); track author.id) {
          <option [value]="author.id">
            {{ author.name }} ({{ author.email }})
          </option>
        }
      </select>
      
      <!-- Custom dropdown arrow -->
      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Empty State (when search returns no results) -->
    @if (filteredAuthors().length === 0 && searchTerm()) {
      <p class="text-xs text-gray-500 dark:text-gray-400 py-1 animate-fadeIn">
        No authors found matching "{{ searchTerm() }}"
      </p>
    }

    <!-- No Authors Available -->
    @if (authors().length === 0) {
      <p class="text-xs text-gray-500 dark:text-gray-400 py-1 animate-fadeIn">
        {{ emptyMessage() }}
      </p>
    }

    <!-- Selected Author Display (optional info) -->
    @if (selectedAuthor()) {
      <div class="text-xs text-gray-600 dark:text-gray-400 py-1 animate-fadeIn">
        Filtering by: <span class="font-medium text-gray-900 dark:text-gray-200">{{ selectedAuthor()!.name }}</span>
      </div>
    }
  }
</div>
